import { MaterialActField } from '@store/models/EntryFields/Field/MaterialActField';
import { EntryTypeFieldName, EntryTypeFieldType, Locale } from 'chaika-enums';

import { store } from '@store';

import { BlockApi } from '@api/BlockApi';
import {
    AllergyField,
    CheckboxListField,
    CheckboxWithTextField,
    CheckboxWithTextListField,
    ComputedField,
    CustomComputedField,
    DateField,
    DiagnosisField,
    DrugField,
    Field,
    LaboratoryTestField,
    LegacyDataField,
    NestedEntryTypeField,
    OptionalTextField,
    PregnancyField,
    ReferralField,
    RenderedServiceField,
    SelectionButtonsField,
    SwitchField,
    TableField,
    TextListField,
} from '@store/models/EntryFields';
import { MedicationField } from '@store/models/EntryFields/Field/MedicationField';
import { PlanDescriptionField } from '@store/models/EntryFields/Field/PlanDescriptionField';
import { FieldData } from '@template/store/entryType/EntryTypeField';
import { TextField } from '@store/models/EntryFields/Field/TextField';

export enum CommonRelation {
    FieldsParams = 'fieldsParams',
}

class CommonService {
    private relationLoader = {
        [CommonRelation.FieldsParams]: this.loadFieldsParam,
    };

    private arrayValueFields = new Set([
        EntryTypeFieldType.CheckboxWithTextList,
        EntryTypeFieldType.CheckboxList,
        EntryTypeFieldType.LegacyData,
        EntryTypeFieldType.NestedEntryType,
        EntryTypeFieldType.MultiSelect,
    ]);

    public async load(relations: CommonRelation[]): Promise<void> {
        await Promise.all(relations.map(relation => this.relationLoader[relation]()));
    }

    public formatUpdateFieldValue(name: string, value: any): any {
        const field = store.common.fieldsParams.get(name);

        let formatted = value;

        if (field && this.arrayValueFields.has(field.type)) {
            formatted = JSON.stringify(value);
        }

        return formatted;
    }

    public createFieldValue(name: string | EntryTypeFieldName, value: any): Field {
        const field = store.common.fieldsParams.get(name);
        let fieldValue;

        if (!field) {
            console.warn(`field with name: ${name} hasn't found in entry type field param`);
        }

        const formattedValue = field ? this.formatFieldValue(field.type, value) : value;

        switch (field && field.type) {
            case EntryTypeFieldType.Table:
                fieldValue = new TableField(formattedValue, name);
                break;
            case EntryTypeFieldType.Allergy:
            case EntryTypeFieldType.DrugAllergy:
                fieldValue = new AllergyField(formattedValue, name);
                break;
            case EntryTypeFieldType.UsedMedications:
                fieldValue = new MedicationField(formattedValue, name);
                break;
            case EntryTypeFieldType.Pregnancy:
                fieldValue = new PregnancyField(formattedValue, name);
                break;
            case EntryTypeFieldType.MedicalCases:
                fieldValue = new DiagnosisField(formattedValue, name);
                break;
            case EntryTypeFieldType.TextList:
                fieldValue = new TextListField(formattedValue, name);
                break;
            case EntryTypeFieldType.OptionalText:
                fieldValue = new OptionalTextField(formattedValue, name);
                break;
            case EntryTypeFieldType.EpisdodesPlanDescription:
                fieldValue = new PlanDescriptionField(formattedValue, name);
                break;
            case EntryTypeFieldType.EpisdodesDrugs:
            case EntryTypeFieldType.Drug:
                fieldValue = new DrugField(formattedValue, name);
                break;
            case EntryTypeFieldType.CheckboxList:
                fieldValue = new CheckboxListField(formattedValue, name);
                break;
            case EntryTypeFieldType.SelectionButtons:
                fieldValue = new SelectionButtonsField(formattedValue, name);
                break;
            case EntryTypeFieldType.Toggle:
                fieldValue = new SwitchField(formattedValue, name);
                break;
            case EntryTypeFieldType.CheckboxWithText:
                fieldValue = new CheckboxWithTextField(formattedValue, name);
                break;
            case EntryTypeFieldType.Computed:
                fieldValue = new ComputedField(formattedValue, name);
                break;
            case EntryTypeFieldType.CustomComputed:
                fieldValue = new CustomComputedField(formattedValue, name);
                break;
            case EntryTypeFieldType.Referrals:
                fieldValue = new ReferralField(formattedValue, name);
                break;
            case EntryTypeFieldType.Date:
                fieldValue = new DateField(formattedValue, name);
                break;
            case EntryTypeFieldType.RenderedService:
                fieldValue = new RenderedServiceField(formattedValue, name);
                break;
            case EntryTypeFieldType.CheckboxWithTextList:
                fieldValue = new CheckboxWithTextListField(formattedValue, name);
                break;
            case EntryTypeFieldType.LegacyData:
                fieldValue = new LegacyDataField(formattedValue, name);
                break;
            case EntryTypeFieldType.NestedEntryType:
                fieldValue = new NestedEntryTypeField(formattedValue, name);
                break;
            case EntryTypeFieldType.LaboratoryTests:
                fieldValue = new LaboratoryTestField(formattedValue, name);
                break;
            case EntryTypeFieldType.Text:
                fieldValue = new TextField(formattedValue, name);
                break;
            case EntryTypeFieldType.MaterialAct:
                fieldValue = new MaterialActField(formattedValue, name);
                break;
            default:
                fieldValue = new Field(formattedValue, name);
                break;
        }

        return fieldValue as Field;
    }

    protected async loadFieldsParam(): Promise<void> {
        const common = store.common;
        const locale = store.authUser.selectedLanguage;

        if (common.fieldsParams.size === 0) {
            const fields = await BlockApi.getFields(locale !== Locale.Ru ? locale : undefined);

            fields.forEach(field => {
                if (!common.fieldsParams.has(field.name)) {
                    common.fieldsParams.set(field.name, {
                        data: field.data as FieldData,
                        title: field.title,
                        type: field.type,
                        global: field.global ?? false,
                    });
                }
            });
        }
    }

    private formatFieldValue(type: EntryTypeFieldType, value: any): any {
        let formatted = value;

        if (this.arrayValueFields.has(type)) {
            if (!Array.isArray(value) && Number.isNaN(Number(value))) {
                // Обертываем в трай кеч тк попадаются кривые данные из медиалога, которые уже не нужна, а страницу ломают
                try {
                    formatted = value
                        ? JSON.parse(
                              value
                                  .replace(/\\n|\\x/g, '')
                                  .replace(/":""/g, '":""""')
                                  .replace(/""/g, '"')
                                  .replace(/\\"/g, '"'),
                          )
                        : [];
                } catch (e: any) {
                    console.error('JSON PARSE ERROR:', type, e.message);
                    console.error('value', value);
                    formatted = [];
                }
            }
        } else if (type === EntryTypeFieldType.MedicalCases) {
            // @ts-ignore
            formatted =
                value &&
                value
                    // @ts-ignore
                    .filter(({ id, value = {}, ...params }) => !!value || (!!params && Object.keys(params).length > 0))
                    // @ts-ignore
                    .map(({ id, value = {}, ...params }) => ({ medicalCaseId: id, ...value, ...params }));
        } else if (type === EntryTypeFieldType.Date) {
            formatted = value?.isDeleted ? null : value;
        }

        return formatted;
    }
}

export const commonService = new CommonService();
